// References — real-folder gallery (category → tab → files) with comments.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../../shared/api/client";
import { PageHeader } from "../../shared/components/PageHeader";
import { Loading } from "../../shared/components/Empty";
import { toast } from "../../shared/stores/toasts";
import { I } from "../../shared/Icon";
import { useAuth } from "../../shared/stores/auth";

// ── Types ────────────────────────────────────────────────────────────────────

type RefFile = {
  name: string;
  category: string;
  tab: string;
  size_bytes: number;
  mime_type: string | null;
  modified_at: string;
  rating_avg?: number;
  rating_count?: number;
};
type RefCategory = { id: number; label: string; order: number; is_trash?: boolean };
type RefTab = { id: number; label: string; order: number; category_id: number };
type Comment = { id: number; author_id: number; author_name: string; body: string; created_at: string | null };

const ALL = "all";
const TRASH_LABEL = "휴지통";
const MAX_FILE_BYTES = 1 * 1024 * 1024 * 1024; // 1 GB

// ── Helpers ───────────────────────────────────────────────────────────────────

function isImage(f: RefFile) {
  if (f.mime_type?.startsWith("image/")) return true;
  return /\.(png|jpe?g|gif|bmp|svg|webp|avif|ico|tiff?)$/i.test(f.name);
}
function isPdf(f: RefFile)   { return f.mime_type === "application/pdf" || /\.pdf$/i.test(f.name); }
function isVideo(f: RefFile) { return (f.mime_type?.startsWith("video/") ?? false) || /\.(mp4|webm|mov|m4v|ogg)$/i.test(f.name); }
/** Filename without its extension (for display). */
function stripExt(name: string) { const i = name.lastIndexOf("."); return i > 0 ? name.slice(0, i) : name; }
/** Documents we can render a first-page thumbnail for. */
function isDoc(f: RefFile) { return /\.(pdf|pptx)$/i.test(f.name); }
/** Files the SERVER can build a thumbnail for (mirrors backend thumbs.can_thumb).
 *  SVG/ICO/AVIF are NOT included → request the raw file directly (avoids 404s). */
const THUMBABLE_RE = /\.(png|jpe?g|gif|webp|bmp|tiff?|pdf|pptx)$/i;
function fmtSize(bytes: number) {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}
function fileEmoji(f: RefFile) {
  if (isImage(f)) return null;
  if (isPdf(f))   return "📄";
  const m = f.mime_type ?? ""; const n = f.name.toLowerCase();
  if (m.includes("word") || n.endsWith(".docx") || n.endsWith(".doc"))         return "📝";
  if (m.includes("excel") || m.includes("spreadsheet") || n.endsWith(".xlsx")) return "📊";
  if (m.includes("powerpoint") || m.includes("presentation"))                  return "📽️";
  if (m.includes("zip") || m.includes("rar") || n.endsWith(".zip"))            return "🗜️";
  if (m.includes("video"))                                                     return "🎬";
  return "📁";
}
function rawUrl(f: RefFile, download = false) {
  const q = `category=${encodeURIComponent(f.category)}&tab=${encodeURIComponent(f.tab)}&name=${encodeURIComponent(f.name)}`;
  return `/references/files/raw?${q}${download ? "&download=true" : ""}`;
}
function thumbUrl(f: RefFile) {
  const q = `category=${encodeURIComponent(f.category)}&tab=${encodeURIComponent(f.tab)}&name=${encodeURIComponent(f.name)}`;
  return `/references/files/thumb?${q}`;
}
const fileKey = (f: RefFile) => `${f.category}/${f.tab}/${f.name}`;

async function downloadFile(f: RefFile) {
  const res = await api.get(rawUrl(f, true), { responseType: "blob" });
  const url = URL.createObjectURL(res.data as Blob);
  const a = document.createElement("a"); a.href = url; a.download = f.name; a.click();
  URL.revokeObjectURL(url);
}

// ── Image thumbnail (lazy blob fetch) ─────────────────────────────────────────

function ImageThumb({ file, onClick, h = "75%", natural, onMissing }: { file: RefFile; onClick?: () => void; h?: string; natural?: boolean; onMissing?: (f: RefFile) => void }) {
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    let url: string; let cancelled = false;
    // Try the small server thumbnail only for formats it supports; else go straight to raw.
    const raw = rawUrl(file);
    const urls = THUMBABLE_RE.test(file.name) ? [thumbUrl(file), raw] : [raw];
    (async () => {
      let rawMissing = false;
      for (const u of urls) {
        try {
          const r = await api.get(u, { responseType: "blob" });
          if (cancelled) return;
          url = URL.createObjectURL(r.data as Blob); setSrc(url); return;
        } catch (e: any) {
          // raw 404 = the file no longer exists on disk (phantom list entry).
          if (u === raw && e?.response?.status === 404) rawMissing = true;
        }
      }
      if (!cancelled) { setSrc("err"); if (rawMissing) onMissing?.(file); }
    })();
    return () => { cancelled = true; if (url) URL.revokeObjectURL(url); };
  }, [file.category, file.tab, file.name]);

  if (src === "err" || src === null) {
    return (
      <div style={{ width: "100%", paddingBottom: natural ? "62%" : h, position: "relative", background: "var(--bg-2,#f3f4f6)", borderRadius: 6 }}>
        <span style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
          {src === null ? <span style={{ fontSize: 11, color: "var(--text-3)" }}>⏳</span> : "🖼️"}
        </span>
      </div>
    );
  }
  if (natural) {
    // Free aspect ratio — full image, no top/bottom cropping (masonry).
    return (
      <div style={{ borderRadius: 6, overflow: "hidden", cursor: onClick ? "zoom-in" : undefined, background: "var(--bg-2,#f3f4f6)" }} onClick={onClick}>
        <img src={src} alt={file.name} draggable={false} style={{ display: "block", width: "100%", height: "auto" }} />
      </div>
    );
  }
  return (
    <div style={{ width: "100%", paddingBottom: h, position: "relative", overflow: "hidden", borderRadius: 6, cursor: onClick ? "zoom-in" : undefined }} onClick={onClick}>
      <img src={src} alt={file.name} draggable={false} style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} />
    </div>
  );
}

// ── Document thumbnail (first page of PDF/PPTX) ───────────────────────────────

function DocThumb({ file, onClick, h = "75%", emoji, natural, onMissing }: { file: RefFile; onClick?: () => void; h?: string; emoji: string; natural?: boolean; onMissing?: (f: RefFile) => void }) {
  const [src, setSrc] = useState<string | "loading" | "err">("loading");
  useEffect(() => {
    let url: string; let cancelled = false;
    api.get(thumbUrl(file), { responseType: "blob" })
      .then((r) => { if (cancelled) return; url = URL.createObjectURL(r.data as Blob); setSrc(url); })
      .catch(async (e: any) => {
        if (cancelled) return;
        setSrc("err");
        // A thumb 404 can mean either generation failed OR the source is gone.
        // Only flag it missing if raw also 404s (the file truly doesn't exist).
        if (e?.response?.status === 404 && onMissing) {
          try { await api.get(rawUrl(file), { responseType: "blob" }); }
          catch (e2: any) { if (!cancelled && e2?.response?.status === 404) onMissing(file); }
        }
      });
    return () => { cancelled = true; if (url) URL.revokeObjectURL(url); };
  }, [file.category, file.tab, file.name]);

  if (src === "loading" || src === "err") {
    return (
      <div style={{ width: "100%", paddingBottom: natural ? "62%" : h, position: "relative", background: "var(--bg-2,#f3f4f6)", borderRadius: 6, cursor: onClick ? "zoom-in" : undefined }} onClick={onClick}>
        <span style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 36 }}>{src === "loading" ? "⏳" : emoji}</span>
      </div>
    );
  }
  if (natural) {
    return (
      <div style={{ position: "relative", borderRadius: 6, overflow: "hidden", cursor: onClick ? "zoom-in" : undefined, background: "#fff" }} onClick={onClick}>
        <img src={src} alt={file.name} draggable={false} style={{ display: "block", width: "100%", height: "auto" }} />
        <span style={{ position: "absolute", bottom: 4, right: 4, fontSize: 11, background: "rgba(0,0,0,0.55)", color: "#fff", borderRadius: 4, padding: "0 4px", lineHeight: 1.6 }}>{emoji}</span>
      </div>
    );
  }
  return (
    <div style={{ width: "100%", paddingBottom: h, position: "relative", overflow: "hidden", borderRadius: 6, cursor: onClick ? "zoom-in" : undefined, background: "#fff" }} onClick={onClick}>
      <img src={src} alt={file.name} draggable={false} style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "contain" }} />
      <span style={{ position: "absolute", bottom: 4, right: 4, fontSize: 11, background: "rgba(0,0,0,0.55)", color: "#fff", borderRadius: 4, padding: "0 4px", lineHeight: 1.6 }}>{emoji}</span>
    </div>
  );
}

// ── Star rating widget ─────────────────────────────────────────────────────────

function Stars({ value, onRate, size = 16 }: { value: number; onRate?: (n: number) => void; size?: number }) {
  const [hover, setHover] = useState(0);
  return (
    <span style={{ display: "inline-flex", gap: 1 }} onMouseLeave={() => setHover(0)}>
      {[1, 2, 3, 4, 5].map((n) => (
        <span key={n}
          onClick={onRate ? (e) => { e.stopPropagation(); onRate(n); } : undefined}
          onMouseEnter={onRate ? () => setHover(n) : undefined}
          style={{ fontSize: size, lineHeight: 1, cursor: onRate ? "pointer" : "default", color: (hover || value) >= n ? "#f59e0b" : "var(--border-strong,#d1d5db)" }}>★</span>
      ))}
    </span>
  );
}

// ── File detail + comments modal ──────────────────────────────────────────────

function FileDetail({ files, initialIndex, onClose, onDeleted, onRenamed, onRated, onMissing }: { files: RefFile[]; initialIndex: number; onClose: () => void; onDeleted: () => void; onRenamed?: () => void; onRated?: () => void; onMissing?: (f: RefFile) => void }) {
  const [idx, setIdx] = useState(initialIndex);
  const [f, setF] = useState<RefFile>(files[initialIndex] ?? files[0]);
  useEffect(() => { setF(files[idx] ?? files[0]); }, [idx, files]);

  const [src, setSrc] = useState<string | null>(null);
  const [natural, setNatural] = useState<{ w: number; h: number } | null>(null);  // intrinsic media size
  const [comments, setComments] = useState<Comment[] | null>(null);
  const [body, setBody] = useState("");
  const [editingName, setEditingName] = useState(false);
  const [draftName, setDraftName] = useState("");
  const [rating, setRating] = useState<{ avg: number; count: number; mine: number } | null>(null);
  const previewRef = useRef<HTMLDivElement>(null);
  const me = useAuth((s) => s.user?.id ?? null);
  const isSuper = useAuth((s) => s.hasRole("SUPER_ADMIN"));

  useEffect(() => {
    setSrc(null);
    setNatural(null);
    let url: string;
    if (isImage(f) || isPdf(f) || isVideo(f)) {
      api.get(rawUrl(f), { responseType: "blob" })
        .then((r) => { url = URL.createObjectURL(r.data as Blob); setSrc(url); })
        .catch((e: any) => { setSrc("err"); if (e?.response?.status === 404) onMissing?.(f); });
    } else setSrc("unsupported");
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [f.category, f.tab, f.name]);

  const loadComments = useCallback(() => {
    const q = `category=${encodeURIComponent(f.category)}&tab=${encodeURIComponent(f.tab)}&name=${encodeURIComponent(f.name)}`;
    api.get<Comment[]>(`/references/comments?${q}`).then((r) => setComments(r.data)).catch(() => setComments([]));
  }, [f.category, f.tab, f.name]);
  useEffect(() => { loadComments(); }, [loadComments]);

  // Ratings
  useEffect(() => {
    const q = `category=${encodeURIComponent(f.category)}&tab=${encodeURIComponent(f.tab)}&name=${encodeURIComponent(f.name)}`;
    api.get<{ avg: number; count: number; mine: number }>(`/references/ratings?${q}`).then((r) => setRating(r.data)).catch(() => setRating(null));
  }, [f.category, f.tab, f.name]);
  const setMyRating = async (n: number) => {
    const stars = rating?.mine === n ? 0 : n;   // click same star to clear
    try {
      const r = await api.put<{ avg: number; count: number; mine: number }>("/references/ratings", { category: f.category, tab: f.tab, name: f.name, stars });
      setRating(r.data); onRated?.();
    } catch { toast.error("별점 저장 실패"); }
  };

  // Rename in place: edit the name (extension hidden); backend keeps the extension.
  const beginRename = () => { setDraftName(stripExt(f.name)); setEditingName(true); };
  const commitRename = async () => {
    setEditingName(false);
    const v = draftName.trim();
    if (!v || v === stripExt(f.name)) return;
    try {
      const r = await api.patch<RefFile>("/references/files/rename", { category: f.category, tab: f.tab, name: f.name, new_name: v });
      setF((cur) => ({ ...cur, name: r.data.name }));
      onRenamed?.();
      toast.success("이름 변경됨");
    } catch (e: any) { toast.error("이름 변경 실패", e?.response?.data?.detail ?? ""); }
  };

  const deleteOpen = async () => {
    const inTrash = f.category === TRASH_LABEL;
    const msg = inTrash
      ? `"${f.name}" 을 영구 삭제하시겠습니까? 복구할 수 없습니다.`
      : `"${f.name}" 을 휴지통으로 이동하시겠습니까?`;
    if (!confirm(msg)) return;
    try {
      await api.delete(`/references/files?category=${encodeURIComponent(f.category)}&tab=${encodeURIComponent(f.tab)}&name=${encodeURIComponent(f.name)}`);
      toast.success(inTrash ? "영구 삭제됨" : "휴지통으로 이동됨"); onDeleted(); onClose();
    } catch { toast.error("삭제 실패"); }
  };

  // F2 → rename; ← → → navigate; Esc → close; Del → delete this file
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.key === "F2") { e.preventDefault(); beginRename(); return; }
      if (editingName) return;   // while renaming, the input handles Esc/Enter
      if (e.key === "Escape") {
        if (document.fullscreenElement) return;   // let the browser exit fullscreen first
        e.preventDefault(); onClose(); return;
      }
      if (e.key === "Delete") { e.preventDefault(); deleteOpen(); return; }
      if (e.key === "ArrowLeft") { e.preventDefault(); setIdx(i => Math.max(0, i - 1)); }
      if (e.key === "ArrowRight") { e.preventDefault(); setIdx(i => Math.min(files.length - 1, i + 1)); }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [editingName, files.length, f.name, onClose]);

  const addComment = async () => {
    if (!body.trim()) return;
    try {
      await api.post("/references/comments", { category: f.category, tab: f.tab, name: f.name, body });
      setBody(""); loadComments();
    } catch { toast.error("댓글 등록 실패"); }
  };
  const delComment = async (id: number) => {
    try { await api.delete(`/references/comments/${id}`); loadComments(); } catch { toast.error("삭제 실패"); }
  };

  // Size the modal to the media so there's no wasted letterboxing. Until the
  // intrinsic size is known (or for PDFs), fall back to a large default box.
  const vw = window.innerWidth, vh = window.innerHeight;
  const SIDE = 320;                              // comments panel width
  const CHROME = 112;                            // header + footer height
  const maxPrevW = Math.max(300, vw * 0.96 - SIDE - 16);
  const maxPrevH = vh * 0.92 - CHROME;
  let prevW = Math.min(maxPrevW, 920);
  let modalH = Math.round(vh * 0.92);
  if (natural) {
    const s = Math.min(maxPrevW / natural.w, maxPrevH / natural.h, 1);  // never upscale past 100%
    prevW = Math.max(300, Math.round(natural.w * s));
    // Keep enough height for the comments panel even for short/wide images.
    modalH = Math.max(Math.min(520, Math.round(vh * 0.92)), Math.round(natural.h * s) + CHROME);
  }
  const modalW = Math.min(Math.round(vw * 0.96), Math.round(prevW + SIDE + 2));

  return (
    <div className="t-overlay" onClick={onClose}>
      <div className="t-cmdk" onClick={(e) => e.stopPropagation()} style={{ width: modalW, height: modalH, maxWidth: "96vw", maxHeight: "92vh", display: "flex", flexDirection: "column", transition: "width 0.18s ease, height 0.18s ease" }}>
        <div style={{ padding: "12px 16px", borderBottom: "1px solid var(--border)", display: "flex", alignItems: "center", gap: 10 }}>
          {editingName ? (
            <input autoFocus className="t-input" style={{ flex: 1, fontSize: 14, fontWeight: 600, padding: "3px 8px" }}
              value={draftName} onChange={(e) => setDraftName(e.target.value)}
              onBlur={commitRename}
              onKeyDown={(e) => { if (e.key === "Enter") commitRename(); if (e.key === "Escape") setEditingName(false); }} />
          ) : (
            <span onClick={beginRename} title="클릭하여 이름 변경 (F2)"
              style={{ flex: 1, fontWeight: 600, fontSize: 14, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", cursor: "text", padding: "2px 4px", borderRadius: 4 }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "var(--panel)")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}>
              {stripExt(f.name)}
            </span>
          )}
          <span style={{ fontSize: 12, color: "var(--text-3)", flexShrink: 0 }}>{f.category} · {f.tab} · {fmtSize(f.size_bytes)}</span>
          <button className="t-btn" onClick={() => setIdx(i => Math.max(0, i - 1))} disabled={idx === 0} title="이전 파일 (←)" style={{ fontSize: 12 }}>◀</button>
          <span style={{ fontSize: 12, color: "var(--text-3)", flexShrink: 0 }}>{idx + 1} / {files.length}</span>
          <button className="t-btn" onClick={() => setIdx(i => Math.min(files.length - 1, i + 1))} disabled={idx === files.length - 1} title="다음 파일 (→)" style={{ fontSize: 12 }}>▶</button>
          <button className="t-btn" onClick={() => downloadFile(f)} style={{ fontSize: 12 }}>⬇️</button>
          <button className="t-btn" onClick={onClose}>✕</button>
        </div>
        <div style={{ display: "flex", flex: 1, minHeight: 0 }}>
          {/* Preview — pane hugs the media's displayed width */}
          <div ref={previewRef} style={{ position: "relative", width: prevW, flexShrink: 0, minWidth: 0, background: "#0b0b0d", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", padding: 8 }}>
            {src && src !== "err" && src !== "unsupported" && (
              <button onClick={() => previewRef.current?.requestFullscreen?.()} title="전체화면"
                style={{ position: "absolute", top: 8, right: 8, zIndex: 3, background: "rgba(0,0,0,0.55)", color: "#fff", border: "none", borderRadius: 6, padding: "5px 10px", cursor: "pointer", fontSize: 13 }}>⛶ 전체화면</button>
            )}
            {src === null && <span style={{ color: "rgba(255,255,255,0.5)", fontSize: 13 }}>불러오는 중…</span>}
            {src === "err" && <span style={{ color: "#ef4444" }}>미리보기 실패</span>}
            {src === "unsupported" && <div style={{ textAlign: "center", color: "#fff" }}><div style={{ fontSize: 48 }}>{fileEmoji(f) ?? "📁"}</div><div style={{ fontSize: 13, marginTop: 8 }}>{f.name}</div></div>}
            {src && src !== "err" && src !== "unsupported" && isImage(f) && (
              <img src={src} alt={f.name} onLoad={(e) => setNatural({ w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight })}
                style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} />
            )}
            {src && src !== "err" && src !== "unsupported" && isVideo(f) && (
              <video src={src} controls onLoadedMetadata={(e) => setNatural({ w: e.currentTarget.videoWidth, h: e.currentTarget.videoHeight })}
                style={{ maxWidth: "100%", maxHeight: "100%" }} />
            )}
            {src && src !== "err" && src !== "unsupported" && isPdf(f) && (
              <iframe src={src} title={f.name} style={{ width: "100%", height: "100%", border: 0 }} />
            )}
          </div>
          {/* Comments */}
          <div style={{ width: 320, flexShrink: 0, borderLeft: "1px solid var(--border)", display: "flex", flexDirection: "column", minHeight: 0 }}>
            <div style={{ padding: "10px 14px", borderBottom: "1px solid var(--border)" }}>
              <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6, color: "var(--text-2)" }}>⭐ 별점</div>
              <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                <Stars value={rating?.mine ?? 0} onRate={setMyRating} size={20} />
                <span style={{ fontSize: 12, color: "var(--text-3)" }}>평균 <b style={{ color: "#f59e0b" }}>★{(rating?.avg ?? 0).toFixed(1)}</b> · {rating?.count ?? 0}명</span>
              </div>
            </div>
            <div style={{ padding: "10px 14px", borderBottom: "1px solid var(--border)", fontWeight: 600, fontSize: 13 }}>
              💬 댓글 · 피드백 {comments ? `(${comments.length})` : ""}
            </div>
            <div style={{ flex: 1, overflowY: "auto", padding: "10px 14px", display: "flex", flexDirection: "column", gap: 10 }}>
              {comments === null && <span style={{ fontSize: 12, color: "var(--text-3)" }}>불러오는 중…</span>}
              {comments?.length === 0 && <span style={{ fontSize: 12, color: "var(--text-3)", textAlign: "center", padding: "20px 0" }}>첫 피드백을 남겨보세요 🙂</span>}
              {comments?.map((c) => (
                <div key={c.id} style={{ fontSize: 13 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 2 }}>
                    <span style={{ fontWeight: 600, fontSize: 12 }}>{c.author_name}</span>
                    <span style={{ fontSize: 11, color: "var(--text-3)" }}>{c.created_at ? new Date(c.created_at).toLocaleString("ko-KR", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : ""}</span>
                    {(me === c.author_id || isSuper) && (
                      <button onClick={() => delComment(c.id)} style={{ marginLeft: "auto", background: "none", border: "none", cursor: "pointer", color: "var(--text-3)", fontSize: 11 }}>삭제</button>
                    )}
                  </div>
                  <div style={{ whiteSpace: "pre-wrap", lineHeight: 1.5, color: "var(--text-1)" }}>{c.body}</div>
                </div>
              ))}
            </div>
            <div style={{ padding: 10, borderTop: "1px solid var(--border)", display: "flex", gap: 6 }}>
              <input className="t-input" style={{ flex: 1, fontSize: 13 }} placeholder="피드백 입력…" value={body}
                onChange={(e) => setBody(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") addComment(); }} />
              <button className="t-btn t-btn-primary" style={{ fontSize: 12 }} onClick={addComment} disabled={!body.trim()}>등록</button>
            </div>
          </div>
        </div>
        <div style={{ padding: "10px 16px", borderTop: "1px solid var(--border)", display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button className="t-btn t-btn-primary" onClick={() => downloadFile(f)} style={{ marginRight: "auto" }}>⬇️ 다운로드</button>
          <button className="t-btn" style={{ color: "#ef4444" }} onClick={deleteOpen}
            title="Del 키로도 삭제">{f.category === TRASH_LABEL ? "영구 삭제" : "휴지통으로"}</button>
          <button className="t-btn" onClick={onClose}>닫기</button>
        </div>
      </div>
    </div>
  );
}

// ── Top Tab bar (tabs of the selected category; manage = 팀장급 이상) ──────────

function TabBar({ categoryId, activeTab, canManage, onSelect, onChanged }: {
  categoryId: number; categoryName: string; activeTab: string;
  canManage: boolean; onSelect: (tab: string) => void; onChanged: () => void;
}) {
  const [tabs, setTabs] = useState<RefTab[]>([]);
  const [adding, setAdding] = useState(false);
  const [newLabel, setNewLabel] = useState("");
  const [editId, setEditId] = useState<number | null>(null);
  const [editLabel, setEditLabel] = useState("");
  const [tabCtx, setTabCtx] = useState<{ x: number; y: number; tab: RefTab; idx: number } | null>(null);
  const dragIdx = useRef<number | null>(null);

  const load = useCallback(() => {
    api.get<RefTab[]>(`/references/tabs?category_id=${categoryId}`).then((r) => setTabs(r.data)).catch(() => setTabs([]));
  }, [categoryId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { const c = () => setTabCtx(null); document.addEventListener("click", c); return () => document.removeEventListener("click", c); }, []);

  const addTab = async () => {
    const label = newLabel.trim();
    if (!label) { setAdding(false); return; }
    try {
      await api.post("/references/tabs", { category_id: categoryId, label });
      setNewLabel(""); setAdding(false); load(); onChanged();
    } catch (e: any) { toast.error("추가 실패", e?.response?.data?.detail ?? ""); }
  };
  const renameTab = async (id: number) => {
    const label = editLabel.trim();
    if (label) { try { await api.patch(`/references/tabs/${id}`, { label }); load(); onChanged(); } catch { toast.error("수정 실패"); } }
    setEditId(null);
  };
  const delTab = async (t: RefTab) => {
    if (!confirm(`"${t.label}" 탭과 그 안의 파일을 삭제하시겠습니까?`)) return;
    try { await api.delete(`/references/tabs/${t.id}`); load(); onChanged(); if (activeTab === t.label) onSelect(ALL); } catch { toast.error("삭제 실패"); }
  };
  const moveTab = async (idx: number, dir: -1 | 1) => {
    const arr = [...tabs]; const j = idx + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    try { await api.put("/references/tabs/reorder", { ids: arr.map((t) => t.id) }); setTabs(arr); onChanged(); } catch { toast.error("순서 변경 실패"); }
  };
  const dropReorder = async (toIdx: number) => {
    const from = dragIdx.current; dragIdx.current = null;
    if (from == null || from === toIdx) return;
    const arr = [...tabs];
    arr.splice(toIdx, 0, arr.splice(from, 1)[0]);
    setTabs(arr);
    try { await api.put("/references/tabs/reorder", { ids: arr.map((t) => t.id) }); onChanged(); } catch { toast.error("순서 변경 실패"); load(); }
  };

  const pill = (active: boolean): React.CSSProperties => ({
    display: "inline-flex", alignItems: "center", gap: 4, padding: "5px 12px", borderRadius: 8,
    fontSize: 13, cursor: "pointer", whiteSpace: "nowrap",
    background: active ? "var(--p-500,#6366f1)" : "var(--panel)",
    color: active ? "#fff" : "var(--text-2)", fontWeight: active ? 600 : 400, border: "none",
  });

  return (
    <div style={{ display: "flex", gap: 6, alignItems: "center", padding: "8px 16px", borderBottom: "1px solid var(--border)", flexWrap: "wrap", flexShrink: 0 }}>
      <button style={pill(activeTab === ALL)} onClick={() => onSelect(ALL)}>전체</button>
      {tabs.map((t, idx) => (
        editId === t.id ? (
          <input key={t.id} autoFocus className="t-input" style={{ width: 110, fontSize: 13, padding: "3px 8px" }}
            value={editLabel} onChange={(e) => setEditLabel(e.target.value)}
            onBlur={() => renameTab(t.id)}
            onKeyDown={(e) => { if (e.key === "Enter") renameTab(t.id); if (e.key === "Escape") setEditId(null); }} />
        ) : (
          <button key={t.id} style={pill(activeTab === t.label)}
            onClick={() => onSelect(t.label)}
            title={canManage ? "드래그로 순서 변경 · 우클릭 메뉴" : undefined}
            draggable={canManage}
            onDragStart={() => { dragIdx.current = idx; }}
            onDragOver={canManage ? (e) => e.preventDefault() : undefined}
            onDrop={canManage ? () => dropReorder(idx) : undefined}
            onContextMenu={(e) => { if (canManage) { e.preventDefault(); e.stopPropagation(); setTabCtx({ x: e.clientX, y: e.clientY, tab: t, idx }); } }}>
            {t.label}
          </button>
        )
      ))}
      {canManage && (adding ? (
        <input autoFocus className="t-input" style={{ width: 120, fontSize: 13, padding: "3px 8px" }}
          value={newLabel} placeholder="새 탭 이름…" onChange={(e) => setNewLabel(e.target.value)}
          onBlur={addTab} onKeyDown={(e) => { if (e.key === "Enter") addTab(); if (e.key === "Escape") { setAdding(false); setNewLabel(""); } }} />
      ) : (
        <button style={{ ...pill(false), color: "var(--p-500,#6366f1)" }} onClick={() => setAdding(true)} title="탭 추가">＋ 탭</button>
      ))}

      {tabCtx && (
        <div onClick={(e) => e.stopPropagation()} style={{ position: "fixed", top: tabCtx.y, left: tabCtx.x, background: "var(--bg-0,#fff)", border: "1px solid var(--border)", borderRadius: 8, padding: "4px 0", zIndex: 9999, boxShadow: "0 4px 20px rgba(0,0,0,.15)", minWidth: 168, fontSize: 13 }}>
          <CtxBtn label="이름 변경" icon="✏️" onClick={() => { setEditId(tabCtx.tab.id); setEditLabel(tabCtx.tab.label); setTabCtx(null); }} />
          {tabCtx.idx > 0 && <CtxBtn label="왼쪽으로 이동" icon="◀️" onClick={() => { moveTab(tabCtx.idx, -1); setTabCtx(null); }} />}
          {tabCtx.idx < tabs.length - 1 && <CtxBtn label="오른쪽으로 이동" icon="▶️" onClick={() => { moveTab(tabCtx.idx, 1); setTabCtx(null); }} />}
          <div style={{ height: 1, background: "var(--border)", margin: "4px 0" }} />
          <CtxBtn label="삭제" icon="🗑️" danger onClick={() => { delTab(tabCtx.tab); setTabCtx(null); }} />
        </div>
      )}
    </div>
  );
}
// ── File rename modal ──────────────────────────────────────────────────────────

function RenameModal({ file, onClose, onDone }: { file: RefFile; onClose: () => void; onDone: () => void }) {
  const [name, setName] = useState(file.name);
  const [saving, setSaving] = useState(false);
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => { ref.current?.focus(); ref.current?.select(); }, []);

  const submit = async () => {
    const v = name.trim();
    if (!v || v === file.name) { onClose(); return; }
    setSaving(true);
    try {
      await api.patch("/references/files/rename", { category: file.category, tab: file.tab, name: file.name, new_name: v });
      toast.success("이름 변경됨"); onDone(); onClose();
    } catch (e: any) { toast.error("이름 변경 실패", e?.response?.data?.detail ?? ""); }
    finally { setSaving(false); }
  };

  return (
    <div className="t-overlay" onClick={onClose}>
      <div className="t-cmdk" onClick={(e) => e.stopPropagation()} style={{ width: 420, maxWidth: "95vw" }}>
        <div style={{ padding: "14px 16px 10px", borderBottom: "1px solid var(--border)" }}>
          <h3 style={{ margin: 0, fontSize: 14 }}>파일 이름 변경</h3>
        </div>
        <div style={{ padding: 16 }}>
          <label className="t-label">새 이름</label>
          <input ref={ref} className="t-input" value={name} onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") submit(); if (e.key === "Escape") onClose(); }} />
          <div style={{ fontSize: 11, color: "var(--text-3)", marginTop: 6 }}>확장자를 생략하면 원래 확장자가 유지됩니다.</div>
        </div>
        <div style={{ padding: "10px 16px", borderTop: "1px solid var(--border)", display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button className="t-btn" onClick={onClose} disabled={saving}>취소</button>
          <button className="t-btn t-btn-primary" onClick={submit} disabled={saving}>{saving ? "변경 중…" : "변경"}</button>
        </div>
      </div>
    </div>
  );
}

// ── Dashboard (overview when catId === ALL) ────────────────────────────────────

function RefDashboard({ files, categories }: { files: RefFile[] | null; categories: RefCategory[] }) {
  if (!files) return <Loading />;
  const totalSize = files.reduce((s, f) => s + f.size_bytes, 0);
  const catStats = categories
    .filter(c => !c.is_trash)
    .map(c => ({
      label: c.label,
      count: files.filter(f => f.category === c.label).length,
      size: files.filter(f => f.category === c.label).reduce((s, f) => s + f.size_bytes, 0),
    }))
    .filter(c => c.count > 0)
    .sort((a, b) => b.count - a.count);
  const recent = [...files].sort((a, b) => b.modified_at.localeCompare(a.modified_at)).slice(0, 10);

  return (
    <div style={{ flex: 1, overflowY: "auto", padding: "20px" }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12, marginBottom: 24 }}>
        {([
          { label: "전체 파일", value: `${files.length}개`, icon: "📁" },
          { label: "카테고리", value: `${catStats.length}개`, icon: "🗂️" },
          { label: "전체 용량", value: fmtSize(totalSize), icon: "💾" },
        ] as const).map(({ label, value, icon }) => (
          <div key={label} className="t-card" style={{ padding: "16px 20px", display: "flex", alignItems: "center", gap: 14 }}>
            <span style={{ fontSize: 28 }}>{icon}</span>
            <div>
              <div style={{ fontSize: 12, color: "var(--text-3)", marginBottom: 2 }}>{label}</div>
              <div style={{ fontSize: 20, fontWeight: 700 }}>{value}</div>
            </div>
          </div>
        ))}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        <div className="t-card" style={{ padding: 16 }}>
          <div style={{ fontWeight: 600, marginBottom: 12, fontSize: 14 }}>카테고리별 현황</div>
          {catStats.length === 0 && <div style={{ color: "var(--text-3)", fontSize: 13 }}>파일 없음</div>}
          {catStats.map(c => (
            <div key={c.label} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
              <span style={{ fontSize: 13, flex: 1, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.label}</span>
              <span style={{ fontSize: 12, color: "var(--text-3)", flexShrink: 0 }}>{c.count}개</span>
              <span style={{ fontSize: 12, color: "var(--text-3)", flexShrink: 0, minWidth: 56, textAlign: "right" }}>{fmtSize(c.size)}</span>
            </div>
          ))}
        </div>
        <div className="t-card" style={{ padding: 16 }}>
          <div style={{ fontWeight: 600, marginBottom: 12, fontSize: 14 }}>최근 업로드</div>
          {recent.length === 0 && <div style={{ color: "var(--text-3)", fontSize: 13 }}>파일 없음</div>}
          {recent.map(f => (
            <div key={fileKey(f)} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
              <span style={{ fontSize: 16, flexShrink: 0 }}>{fileEmoji(f) ?? "🖼️"}</span>
              <span style={{ fontSize: 12, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={f.name}>{stripExt(f.name)}</span>
              <span style={{ fontSize: 11, color: "var(--text-3)", flexShrink: 0 }}>{f.category}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function ReferencesPage() {
  const [params, setParams] = useSearchParams();
  const catId = params.get("filter") || ALL;
  const activeTab = params.get("tab") || ALL;

  const [categories, setCategories] = useState<RefCategory[]>([]);
  const [files, setFiles] = useState<RefFile[] | null>(null);
  // File keys whose raw fetch returned 404 — phantom entries (gone from disk);
  // hidden client-side so a stale list self-heals without a refresh loop.
  const [missing, setMissing] = useState<Set<string>>(new Set());
  const [viewMode, setViewMode] = useState<"gallery" | "list">(() => (localStorage.getItem("ref-view") as any) || "gallery");
  const [detail, setDetail] = useState<RefFile | null>(null);
  const [renaming, setRenaming] = useState<RefFile | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [ctxMenu, setCtxMenu] = useState<{ x: number; y: number; file: RefFile } | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [cols, setCols] = useState<number>(() => { const v = Number(localStorage.getItem("ref-cols")); return v >= 1 && v <= 9 ? v : 6; });
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<"name" | "modified" | "size" | "rating">(() => (localStorage.getItem("ref-sort") as any) || "name");
  const lastIdx = useRef<number | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dragCounter = useRef(0);

  const changeCols = (n: number) => { const v = Math.max(1, Math.min(9, n)); setCols(v); localStorage.setItem("ref-cols", String(v)); };

  const canManage = useAuth((s) => s.canManageMenu("references") || s.hasRole("SUPER_ADMIN"));

  const cat = categories.find((c) => String(c.id) === catId) ?? null;
  const catName = catId === ALL ? null : (cat?.label ?? null);
  const isTrashView = cat?.is_trash === true || cat?.label === TRASH_LABEL;
  // A concrete tab is selected (not 전체) → uploads/paste target it.
  const tabName = activeTab === ALL ? null : activeTab;

  const setView = (v: "gallery" | "list") => { setViewMode(v); localStorage.setItem("ref-view", v); };
  const setTab = (t: string) => setParams((p) => { const n = new URLSearchParams(p); if (t === ALL) n.delete("tab"); else n.set("tab", t); return n; }, { replace: false });
  const onCategoriesChanged = () => { loadCategories(); window.dispatchEvent(new Event("reference-categories-updated")); };

  const loadCategories = useCallback(() => {
    api.get<RefCategory[]>("/references/categories").then((r) => setCategories(r.data)).catch(() => setCategories([]));
  }, []);
  useEffect(() => {
    loadCategories();
    const h = () => loadCategories();
    window.addEventListener("reference-categories-updated", h);
    return () => window.removeEventListener("reference-categories-updated", h);
  }, [loadCategories]);

  const loadFiles = useCallback(async () => {
    setFiles(null);
    setMissing(new Set());
    try {
      const qs = new URLSearchParams();
      if (catName) qs.set("category", catName);
      if (tabName) qs.set("tab", tabName);
      const r = await api.get<RefFile[]>(`/references/files?${qs}`);
      setFiles(r.data);
    } catch { setFiles([]); }
  }, [catName, tabName]);
  useEffect(() => { loadFiles(); }, [loadFiles, categories]);

  const refresh = () => loadFiles();
  const markMissing = useCallback((f: RefFile) => {
    setMissing((prev) => { if (prev.has(fileKey(f))) return prev; const n = new Set(prev); n.add(fileKey(f)); return n; });
  }, []);

  // ── Upload (drop / pick) into the current tab ────────────────────────────────
  // Resolve the upload target tab. On "전체" (no tab), upload to the category's FIRST tab.
  const resolveUploadTab = async (): Promise<string | null> => {
    if (tabName) return tabName;
    if (!cat) return null;
    try {
      const r = await api.get<RefTab[]>(`/references/tabs?category_id=${cat.id}`);
      return r.data[0]?.label ?? null;
    } catch { return null; }
  };

  const doUpload = async (picked: File[]) => {
    if (!catName) { toast.error("카테고리를 선택하세요", "왼쪽 메뉴에서 카테고리를 고르세요"); return; }
    // Client-side 1 GB guard — reject oversized files before uploading.
    picked.filter(f => f.size > MAX_FILE_BYTES).forEach(f => toast.error("파일 크기 초과", `${f.name} — 최대 1 GB`));
    const valid = picked.filter(f => f.size <= MAX_FILE_BYTES);
    if (!valid.length) return;
    const target = await resolveUploadTab();
    if (!target) { toast.error("탭이 없습니다", "이 카테고리에 탭을 먼저 추가하세요"); return; }
    // Upload in batches of 5 — prevents overwhelming the server with 30+ simultaneous requests.
    const BATCH = 5;
    let ok = 0; let skipped = 0;
    for (let i = 0; i < valid.length; i += BATCH) {
      const batch = valid.slice(i, i + BATCH);
      const results = await Promise.allSettled(
        batch.map(file => {
          const fd = new FormData();
          fd.append("category", catName); fd.append("tab", target); fd.append("file", file);
          return api.post("/references/files", fd, { headers: { "Content-Type": "multipart/form-data" } });
        })
      );
      for (let j = 0; j < results.length; j++) {
        const r = results[j];
        // Same name + same size → backend skips the write and flags it.
        if (r.status === "fulfilled") { if ((r.value as any)?.data?.skipped) skipped++; else ok++; }
        else toast.error("업로드 실패", (r.reason as any)?.response?.data?.detail ?? batch[j].name);
      }
    }
    if (ok || skipped) {
      const parts: string[] = [];
      if (ok) parts.push(`${ok}개 업로드`);
      if (skipped) parts.push(`${skipped}개 중복 건너뜀`);
      toast.success(parts.join(" · "), tabName ? undefined : `'${target}' 탭에 추가됨`);
      refresh();
    }
  };

  const emptyTrash = async () => {
    if (!confirm("휴지통을 비우시겠습니까? 복구할 수 없습니다.")) return;
    try {
      await api.delete("/references/trash");
      toast.success("휴지통 비워짐"); refresh();
    } catch { toast.error("휴지통 비우기 실패"); }
  };

  const syncFilesystem = async () => {
    try {
      const r = await api.post<{ added_categories: number; added_tabs: number }>("/references/sync");
      const { added_categories: cats, added_tabs: tabs } = r.data;
      if (cats || tabs) {
        toast.success("동기화 완료", `카테고리 ${cats}개·탭 ${tabs}개 추가됨`);
        onCategoriesChanged();
      } else {
        toast.success("동기화 완료", "새로운 폴더 없음");
      }
    } catch { toast.error("동기화 실패"); }
  };

  // ── Paste from clipboard (image blob or image URL) ───────────────────────────
  const pasteFromClipboard = async () => {
    if (!catName) { toast.error("카테고리를 선택하세요", "왼쪽 메뉴에서 카테고리를 고르세요"); return; }
    const target = await resolveUploadTab();
    if (!target) { toast.error("탭이 없습니다", "이 카테고리에 탭을 먼저 추가하세요"); return; }
    try {
      if (navigator.clipboard?.read) {
        const items = await navigator.clipboard.read();
        for (const it of items) {
          const t = it.types.find((x) => x.startsWith("image/"));
          if (t) {
            const blob = await it.getType(t);
            const ext = t.split("/")[1] || "png";
            const fd = new FormData();
            fd.append("category", catName); fd.append("tab", target);
            fd.append("file", new File([blob], `clipboard-${Date.now()}.${ext}`, { type: t }));
            await api.post("/references/files", fd, { headers: { "Content-Type": "multipart/form-data" } });
            toast.success("클립보드 이미지 등록됨"); refresh(); return;
          }
        }
      }
      const url = (await navigator.clipboard.readText() || "").trim();
      // YouTube link → download the video (server-side, may take a while).
      if (/(youtube\.com|youtu\.be)/i.test(url)) {
        toast.success("유튜브 다운로드 중…", "영상 길이에 따라 시간이 걸릴 수 있습니다");
        try {
          await api.post("/references/files/from-youtube", { category: catName, tab: target, url }, { timeout: 300000 });
          toast.success("유튜브 영상 등록됨"); refresh();
        } catch (e: any) { toast.error("유튜브 등록 실패", e?.response?.data?.detail ?? ""); }
        return;
      }
      if (url && /^https?:\/\//i.test(url)) {
        await api.post("/references/files/from-url", { category: catName, tab: target, url });
        toast.success("URL 이미지 등록됨"); refresh(); return;
      }
      toast.error("클립보드에 이미지/링크가 없습니다", "이미지·이미지 URL·유튜브 링크를 복사하세요");
    } catch {
      toast.error("등록 실패", "브라우저 클립보드 접근을 허용해주세요");
    }
  };

  const deleteFile = async (f: RefFile) => {
    const inTrash = f.category === TRASH_LABEL;
    const msg = inTrash
      ? `"${f.name}" 을 영구 삭제하시겠습니까? 복구할 수 없습니다.`
      : `"${f.name}" 을 휴지통으로 이동하시겠습니까?`;
    if (!confirm(msg)) return;
    try {
      await api.delete(`/references/files?category=${encodeURIComponent(f.category)}&tab=${encodeURIComponent(f.tab)}&name=${encodeURIComponent(f.name)}`);
      toast.success(inTrash ? "영구 삭제됨" : "휴지통으로 이동됨"); refresh();
    } catch { toast.error("삭제 실패"); }
  };

  // Drag-drop
  const onDragEnter = (e: React.DragEvent) => { e.preventDefault(); dragCounter.current++; if (e.dataTransfer.types.includes("Files")) setDragOver(true); };
  const onDragLeave = () => { dragCounter.current--; if (dragCounter.current === 0) setDragOver(false); };
  const onDrop = (e: React.DragEvent) => {
    e.preventDefault(); dragCounter.current = 0; setDragOver(false);
    // Internal reference-image drags must NOT upload a copy — they only move (to a folder).
    if (e.dataTransfer.types.includes("application/x-ref-file")) return;
    const d = Array.from(e.dataTransfer.files); if (d.length) doUpload(d);
  };

  useEffect(() => { const close = () => setCtxMenu(null); document.addEventListener("click", close); return () => document.removeEventListener("click", close); }, []);
  // Refresh when a file is moved via the left sidebar (drag-to-category).
  useEffect(() => { const h = () => { loadFiles(); setSelected(new Set()); }; window.addEventListener("reference-files-updated", h); return () => window.removeEventListener("reference-files-updated", h); }, [loadFiles]);

  const headerLabel = catId === ALL ? "레퍼런스" : (cat?.label ?? "레퍼런스");

  // ── Search + sort ─────────────────────────────────────────────────────────────
  const changeSort = (s: "name" | "modified" | "size" | "rating") => { setSort(s); localStorage.setItem("ref-sort", s); };
  const displayFiles = useMemo(() => {
    const q = search.trim().toLowerCase();
    const arr = (files ?? []).filter((f) => (!q || f.name.toLowerCase().includes(q)) && !missing.has(fileKey(f)));
    if (sort === "name") arr.sort((a, b) => a.name.localeCompare(b.name, "ko"));
    else if (sort === "modified") arr.sort((a, b) => (b.modified_at || "").localeCompare(a.modified_at || ""));
    else if (sort === "size") arr.sort((a, b) => b.size_bytes - a.size_bytes);
    else if (sort === "rating") arr.sort((a, b) => (b.rating_avg ?? 0) - (a.rating_avg ?? 0) || (b.rating_count ?? 0) - (a.rating_count ?? 0));
    return arr;
  }, [files, search, sort, missing]);

  // ── Multi-select (checkbox / Ctrl / Shift) ───────────────────────────────────
  const shown = displayFiles;
  // Clear selection whenever the visible set changes (category/tab switch).
  useEffect(() => { setSelected(new Set()); lastIdx.current = null; }, [catName, tabName]);

  const isSel = (f: RefFile) => selected.has(fileKey(f));
  const toggleSel = (f: RefFile, idx: number) => {
    setSelected(prev => { const n = new Set(prev); const k = fileKey(f); n.has(k) ? n.delete(k) : n.add(k); return n; });
    lastIdx.current = idx;
  };
  const onCardClick = (f: RefFile, idx: number, e: React.MouseEvent) => {
    if (e.shiftKey && lastIdx.current != null) {
      const a = Math.min(lastIdx.current, idx), b = Math.max(lastIdx.current, idx);
      setSelected(prev => { const n = new Set(prev); for (let i = a; i <= b; i++) if (shown[i]) n.add(fileKey(shown[i])); return n; });
    } else if (e.ctrlKey || e.metaKey) {
      toggleSel(f, idx);
    } else {
      setDetail(f);
    }
  };
  // ── Marquee (rubber-band) selection in the gallery ──────────────────────────
  const gridRef = useRef<HTMLDivElement>(null);
  const [marquee, setMarquee] = useState<{ x0: number; y0: number; x1: number; y1: number } | null>(null);
  const marqueeStart = useRef<{ x: number; y: number } | null>(null);
  const onGridMouseDown = (e: React.MouseEvent) => {
    if (e.button !== 0 || e.target !== gridRef.current) return;   // only from empty grid space
    e.preventDefault();
    marqueeStart.current = { x: e.clientX, y: e.clientY };
    const onMove = (ev: MouseEvent) => {
      const s = marqueeStart.current; if (!s) return;
      const box = { x0: Math.min(s.x, ev.clientX), y0: Math.min(s.y, ev.clientY), x1: Math.max(s.x, ev.clientX), y1: Math.max(s.y, ev.clientY) };
      setMarquee(box);
      const crossing = ev.clientX < s.x;   // drag leftwards = crossing (any touch); rightwards = contained
      const sel = new Set<string>();
      gridRef.current?.querySelectorAll<HTMLElement>("[data-refkey]").forEach((el) => {
        const r = el.getBoundingClientRect();
        const intersect = !(r.right < box.x0 || r.left > box.x1 || r.bottom < box.y0 || r.top > box.y1);
        const contained = r.left >= box.x0 && r.right <= box.x1 && r.top >= box.y0 && r.bottom <= box.y1;
        if (crossing ? intersect : contained) sel.add(el.dataset.refkey!);
      });
      setSelected(sel);
    };
    const onUp = () => { marqueeStart.current = null; setMarquee(null); window.removeEventListener("mousemove", onMove); window.removeEventListener("mouseup", onUp); };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  const selectedFiles = () => shown.filter(isSel);
  const bulkDelete = async () => {
    const fs = selectedFiles(); if (!fs.length) return;
    const allInTrash = fs.every(f => f.category === TRASH_LABEL);
    const msg = allInTrash
      ? `선택한 ${fs.length}개 파일을 영구 삭제하시겠습니까? 복구할 수 없습니다.`
      : `선택한 ${fs.length}개 파일을 휴지통으로 이동하시겠습니까?`;
    if (!confirm(msg)) return;
    for (const f of fs) {
      try { await api.delete(`/references/files?category=${encodeURIComponent(f.category)}&tab=${encodeURIComponent(f.tab)}&name=${encodeURIComponent(f.name)}`); } catch { /* keep going */ }
    }
    toast.success(allInTrash ? `${fs.length}개 영구 삭제됨` : `${fs.length}개 휴지통으로 이동됨`);
    setSelected(new Set()); refresh();
  };
  const bulkDownload = () => selectedFiles().forEach(downloadFile);

  // Del key → delete the selected files (grid). The detail modal handles its own Del.
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.key !== "Delete") return;
      if (detail || renaming) return;   // a modal is open → it handles Del itself
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable)) return;
      if (selected.size === 0) return;
      e.preventDefault();
      bulkDelete();
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [detail, renaming, selected, displayFiles]);

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column", position: "relative" }}
      onDragEnter={onDragEnter} onDragLeave={onDragLeave} onDragOver={(e) => e.preventDefault()} onDrop={onDrop}>
      {dragOver && (
        <div style={{ position: "absolute", inset: 0, zIndex: 500, background: "rgba(99,102,241,0.12)", border: "3px dashed var(--p-400,#818cf8)", borderRadius: 12, display: "flex", alignItems: "center", justifyContent: "center", pointerEvents: "none" }}>
          <div style={{ textAlign: "center", color: "var(--p-500,#6366f1)" }}>
            <div style={{ fontSize: 40 }}>📂</div>
            <div style={{ fontSize: 16, fontWeight: 600 }}>{catName ? (tabName ? `'${tabName}' 탭에 업로드` : `'${catName}'의 첫 탭에 업로드`) : "카테고리를 먼저 선택하세요"}</div>
          </div>
        </div>
      )}

      <PageHeader
        title={headerLabel}
        subtitle={catId === ALL ? "대시보드" : `${cat?.label ?? ""}${tabName ? ` · ${tabName}` : ""}`}
        right={<div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          {viewMode === "gallery" && (
            <div style={{ display: "flex", alignItems: "center", gap: 7, height: 32, padding: "0 10px", border: "1px solid var(--border)", borderRadius: 8, background: "var(--panel)" }} title="드래그하여 한 줄에 보이는 이미지 개수 조절">
              <span style={{ fontSize: 11, color: "var(--text-3)", whiteSpace: "nowrap" }}>열</span>
              <input type="range" min={1} max={9} step={1} value={cols} onChange={(e) => changeCols(Number(e.target.value))}
                style={{ width: 96, accentColor: "var(--p-500,#6366f1)", cursor: "pointer" }} />
              <span style={{ fontSize: 12, fontWeight: 700, color: "var(--text-2)", width: 10, textAlign: "center" }}>{cols}</span>
            </div>
          )}
          <div style={{ display: "flex", border: "1px solid var(--border)", borderRadius: 8, overflow: "hidden" }}>
            <button className="t-btn" onClick={() => setView("gallery")} title="갤러리 보기"
              style={{ border: "none", borderRadius: 0, background: viewMode === "gallery" ? "var(--p-500,#6366f1)" : "var(--panel)", color: viewMode === "gallery" ? "#fff" : undefined, padding: "5px 10px" }}>▦</button>
            <button className="t-btn" onClick={() => setView("list")} title="목록 보기"
              style={{ border: "none", borderRadius: 0, background: viewMode === "list" ? "var(--p-500,#6366f1)" : "var(--panel)", color: viewMode === "list" ? "#fff" : undefined, padding: "5px 10px" }}>☰</button>
          </div>
          {canManage && (
            <button className="t-btn" onClick={syncFilesystem} title="서버 폴더와 동기화">🔄 동기화</button>
          )}
          {isTrashView && canManage && (
            <button className="t-btn" style={{ color: "#ef4444" }} onClick={emptyTrash}>🗑️ 휴지통 비우기</button>
          )}
          <input ref={inputRef} type="file" multiple hidden onChange={(e) => { const p = Array.from(e.target.files ?? []); if (p.length) doUpload(p); if (inputRef.current) inputRef.current.value = ""; }} />
          {!isTrashView && (
            <button className="t-btn t-btn-primary" onClick={() => inputRef.current?.click()}><I.Plus size={14} /> 파일 추가</button>
          )}
        </div>}
      />

      {/* Top tabs — only for a concrete category (categories live in the left subnav) */}
      {catId !== ALL && cat && (
        <TabBar categoryId={cat.id} categoryName={cat.label} activeTab={activeTab} canManage={canManage}
          onSelect={setTab} onChanged={refresh} />
      )}

      {/* Dashboard or file grid */}
      {catId === ALL ? (
        <RefDashboard files={files} categories={categories} />
      ) : null}

      {/* Files (hidden when on dashboard) */}
      <div style={{ flex: catId === ALL ? "0 0 0" : 1, overflowY: "auto", padding: "16px 20px", display: catId === ALL ? "none" : undefined }}>
        {/* Search + sort */}
        {files && files.length > 0 && (
          <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 12, flexWrap: "wrap" }}>
            <div style={{ position: "relative", flex: "0 1 280px", minWidth: 180 }}>
              <span style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", fontSize: 13, color: "var(--text-3)", pointerEvents: "none" }}>🔍</span>
              <input className="t-input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="파일명 검색"
                style={{ height: 34, fontSize: 13, paddingLeft: 30, width: "100%", boxSizing: "border-box" }} />
            </div>
            <select value={sort} onChange={(e) => changeSort(e.target.value as any)} title="정렬 방식"
              style={{ height: 34, borderRadius: 8, border: "1px solid var(--border)", background: "var(--panel)", color: "var(--text-2)", fontSize: 13, padding: "0 8px", cursor: "pointer" }}>
              <option value="name">이름순</option>
              <option value="modified">최신순</option>
              <option value="size">크기순</option>
              <option value="rating">별점순</option>
            </select>
            <span style={{ fontSize: 12, color: "var(--text-3)", marginLeft: "auto" }}>{search ? `${displayFiles.length} / ${files.length}개` : `${files.length}개`}</span>
          </div>
        )}
        {/* Selection toolbar */}
        {selected.size > 0 && (
          <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", marginBottom: 12, background: "var(--p-50,#eef2ff)", border: "1px solid var(--p-200,#c7d2fe)", borderRadius: 8, position: "sticky", top: -16, zIndex: 5 }}>
            <span style={{ fontWeight: 700, fontSize: 13, color: "var(--p-700,#4338ca)" }}>{selected.size}개 선택</span>
            <button className="t-btn" onClick={() => setSelected(new Set(shown.map(fileKey)))}>전체 선택</button>
            <button className="t-btn" onClick={bulkDownload}>⬇️ 다운로드</button>
            <button className="t-btn" style={{ color: "#ef4444" }} onClick={bulkDelete}>{isTrashView ? "🗑️ 영구 삭제" : "🗑️ 휴지통으로"}</button>
            <button className="t-btn" style={{ marginLeft: "auto" }} onClick={() => setSelected(new Set())}>선택 해제</button>
          </div>
        )}
        {files === null && <Loading />}
        {files?.length === 0 && (
          <div style={{ textAlign: "center", padding: "60px 20px", color: "var(--text-3)" }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>📂</div>
            <div style={{ fontSize: 14, fontWeight: 500, color: "var(--text-2)" }}>파일이 없습니다</div>
            <div style={{ fontSize: 13, marginTop: 6 }}>드래그하거나 '파일 추가', 또는 빈 칸을 눌러 클립보드 이미지를 등록하세요</div>
          </div>
        )}

        {files && files.length >= 0 && viewMode === "gallery" && (
          <div ref={gridRef} onMouseDown={onGridMouseDown} style={{ columnCount: cols, columnGap: 14, minHeight: 120 }}>
            {/* paste tile — visible whenever a (non-trash) category is selected */}
            {catName && !isTrashView && (
              <button onClick={pasteFromClipboard} title="클립보드 이미지/링크 등록"
                style={{ width: "100%", breakInside: "avoid", marginBottom: 14, background: "var(--panel)", border: "2px dashed var(--border)", borderRadius: 10, cursor: "pointer", minHeight: 150, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 6, color: "var(--text-3)" }}>
                <span style={{ fontSize: 28 }}>📋</span>
                <span style={{ fontSize: 12 }}>붙여넣기 등록</span>
                <span style={{ fontSize: 10 }}>이미지/링크 복사 후 클릭</span>
              </button>
            )}
            {displayFiles.map((f, idx) => (
              <div key={fileKey(f)} className="t-ref-card"
                data-refkey={fileKey(f)}
                draggable
                onDragStart={(e) => { const sel = isSel(f) ? selectedFiles() : [f]; e.dataTransfer.setData("application/x-ref-file", JSON.stringify(sel.map(x => ({ category: x.category, tab: x.tab, name: x.name })))); e.dataTransfer.effectAllowed = "move"; }}
                style={{ width: "100%", breakInside: "avoid", marginBottom: 14, position: "relative", background: "var(--bg-0,#fff)", border: "1px solid var(--border)", borderRadius: 10, overflow: "hidden", cursor: "pointer", transition: "box-shadow .15s, transform .15s", outline: isSel(f) ? "2px solid var(--p-500,#6366f1)" : undefined, outlineOffset: -2 }}
                onMouseEnter={(e) => { e.currentTarget.style.boxShadow = "0 4px 16px rgba(0,0,0,.12)"; e.currentTarget.style.transform = "translateY(-2px)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "none"; e.currentTarget.style.transform = "none"; }}
                onClick={(e) => onCardClick(f, idx, e)}
                onContextMenu={(e) => { e.preventDefault(); setCtxMenu({ x: e.clientX, y: e.clientY, file: f }); }}>
                <div onClick={(e) => { e.stopPropagation(); toggleSel(f, idx); }} title="선택 (Ctrl/Shift 클릭으로 다중 선택)"
                  style={{ position: "absolute", top: 6, left: 6, zIndex: 2, width: 20, height: 20, borderRadius: 5, border: "2px solid #fff", background: isSel(f) ? "var(--p-500,#6366f1)" : "rgba(0,0,0,0.28)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", boxShadow: "0 1px 3px rgba(0,0,0,.35)" }}>
                  {isSel(f) && <span style={{ color: "#fff", fontSize: 12, fontWeight: 900, lineHeight: 1 }}>✓</span>}
                </div>
                {isImage(f)
                  ? <ImageThumb file={f} onClick={() => setDetail(f)} natural onMissing={markMissing} />
                  : isDoc(f)
                    ? <DocThumb file={f} onClick={() => setDetail(f)} emoji={fileEmoji(f) ?? "📄"} natural onMissing={markMissing} />
                    : <div style={{ width: "100%", paddingBottom: "75%", position: "relative", background: "var(--bg-2,#f3f4f6)" }}>
                        <span style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 36 }}>{fileEmoji(f) ?? "📁"}</span>
                      </div>}
                <div style={{ padding: "8px 10px" }}>
                  <div style={{ fontSize: 12, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={f.name}>{stripExt(f.name)}</div>
                  <div style={{ fontSize: 11, color: "var(--text-3)", marginTop: 2, display: "flex", justifyContent: "space-between" }}>
                    <span>{catId === ALL ? `${f.category}/${f.tab}` : f.tab}</span>
                    <span>{fmtSize(f.size_bytes)}</span>
                  </div>
                  {(f.rating_count ?? 0) > 0 && (
                    <div style={{ fontSize: 11, marginTop: 3, color: "#f59e0b" }}>★ {f.rating_avg}<span style={{ color: "var(--text-3)" }}> ({f.rating_count})</span></div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {files && displayFiles.length > 0 && viewMode === "list" && (
          <div className="t-card" style={{ padding: 0, overflow: "hidden" }}>
            <div style={{ display: "grid", gridTemplateColumns: "44px 1fr 140px 90px 90px 40px", padding: "8px 14px", borderBottom: "2px solid var(--border)", fontSize: 11, fontWeight: 600, color: "var(--text-3)", textTransform: "uppercase", letterSpacing: ".04em", gap: 10 }}>
              <span></span><span>이름</span><span>위치</span><span style={{ textAlign: "right" }}>크기</span><span style={{ textAlign: "center" }}>수정</span><span></span>
            </div>
            {displayFiles.map((f, idx) => (
              <div key={fileKey(f)} style={{ display: "grid", gridTemplateColumns: "44px 1fr 140px 90px 90px 40px", alignItems: "center", padding: "6px 14px", borderBottom: "1px solid var(--border)", gap: 10, cursor: "pointer", background: isSel(f) ? "rgba(99,102,241,0.1)" : undefined }}
                draggable
                onDragStart={(e) => { const sel = isSel(f) ? selectedFiles() : [f]; e.dataTransfer.setData("application/x-ref-file", JSON.stringify(sel.map(x => ({ category: x.category, tab: x.tab, name: x.name })))); e.dataTransfer.effectAllowed = "move"; }}
                onClick={(e) => onCardClick(f, idx, e)}
                onContextMenu={(e) => { e.preventDefault(); setCtxMenu({ x: e.clientX, y: e.clientY, file: f }); }}>
                <div onClick={(e) => { e.stopPropagation(); toggleSel(f, idx); }} title="선택"
                  style={{ position: "relative", width: 36, height: 28, borderRadius: 4, overflow: "hidden", background: "var(--bg-2,#f3f4f6)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, cursor: "pointer", outline: isSel(f) ? "2px solid var(--p-500,#6366f1)" : undefined }}>
                  {isImage(f) ? <ImageThumb file={f} h="100%" onMissing={markMissing} /> : isDoc(f) ? <DocThumb file={f} h="100%" emoji={fileEmoji(f) ?? "📄"} onMissing={markMissing} /> : (fileEmoji(f) ?? "📁")}
                  {isSel(f) && <span style={{ position: "absolute", inset: 0, background: "rgba(99,102,241,0.45)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 900, fontSize: 14 }}>✓</span>}
                </div>
                <span style={{ fontSize: 13, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={f.name}>{stripExt(f.name)}</span>
                <span style={{ fontSize: 12, color: "var(--text-3)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.category}/{f.tab}{(f.rating_count ?? 0) > 0 ? `  ·  ★${f.rating_avg}` : ""}</span>
                <span style={{ fontSize: 12, color: "var(--text-3)", textAlign: "right" }}>{fmtSize(f.size_bytes)}</span>
                <span style={{ fontSize: 11, color: "var(--text-3)", textAlign: "center" }}>{new Date(f.modified_at).toLocaleDateString("ko-KR", { month: "short", day: "numeric" })}</span>
                <span style={{ textAlign: "center", color: "var(--text-3)" }} onClick={(e) => { e.stopPropagation(); setCtxMenu({ x: e.clientX, y: e.clientY, file: f }); }}>⋯</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Context menu */}
      {ctxMenu && (
        <div style={{ position: "fixed", top: ctxMenu.y, left: ctxMenu.x, background: "var(--bg-0,#fff)", border: "1px solid var(--border)", borderRadius: 8, padding: "4px 0", zIndex: 9999, boxShadow: "0 4px 20px rgba(0,0,0,.15)", minWidth: 160, fontSize: 13 }}
          onClick={(e) => e.stopPropagation()}>
          <CtxBtn label="열기 / 댓글" icon="💬" onClick={() => { setDetail(ctxMenu.file); setCtxMenu(null); }} />
          <CtxBtn label="이름 변경" icon="✏️" onClick={() => { setRenaming(ctxMenu.file); setCtxMenu(null); }} />
          <CtxBtn label="다운로드" icon="⬇️" onClick={() => { downloadFile(ctxMenu.file); setCtxMenu(null); }} />
          <div style={{ height: 1, background: "var(--border)", margin: "4px 0" }} />
          <CtxBtn label={ctxMenu.file.category === TRASH_LABEL ? "영구 삭제" : "휴지통으로"} icon="🗑️" danger onClick={() => { deleteFile(ctxMenu.file); setCtxMenu(null); }} />
        </div>
      )}

      {detail && <FileDetail files={displayFiles} initialIndex={Math.max(0, displayFiles.findIndex(fi => fileKey(fi) === fileKey(detail!)))} onClose={() => setDetail(null)} onDeleted={refresh} onRenamed={refresh} onRated={refresh} onMissing={markMissing} />}

      {renaming && <RenameModal file={renaming} onClose={() => setRenaming(null)} onDone={() => { refresh(); setDetail(null); }} />}

      {/* Marquee selection rectangle */}
      {marquee && (
        <div style={{ position: "fixed", left: marquee.x0, top: marquee.y0, width: marquee.x1 - marquee.x0, height: marquee.y1 - marquee.y0, background: "rgba(99,102,241,0.12)", border: "1.5px solid var(--p-500,#6366f1)", borderRadius: 2, zIndex: 9998, pointerEvents: "none" }} />
      )}
    </div>
  );
}

function CtxBtn({ label, icon, onClick, danger }: { label: string; icon: string; onClick: () => void; danger?: boolean }) {
  return (
    <button onClick={onClick} style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", padding: "7px 14px", fontSize: 13, border: "none", cursor: "pointer", background: "transparent", textAlign: "left", color: danger ? "#ef4444" : "var(--text-1)" }}
      onMouseEnter={(e) => (e.currentTarget.style.background = "var(--panel)")} onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}>
      <span>{icon}</span><span>{label}</span>
    </button>
  );
}
